import { useEffect, useMemo, useRef, useState } from "react";
import { FormControl, FormLabel } from "@mui/material";
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  InsertThematicBreak,
  ListsToggle,
  MDXEditor,
  Separator,
  UndoRedo,
  addSyntaxExtension$,
  headingsPlugin,
  listsPlugin,
  markdownShortcutPlugin,
  quotePlugin,
  realmPlugin,
  thematicBreakPlugin,
  toolbarPlugin,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { unsupportedMarkdownConstructs } from "../utils/markdown";

// see unsupportedMarkdownConstructs for why these are turned off rather than left to the plugins
const plainTextForUnsupportedPlugin = realmPlugin({
  init: (realm) => realm.pub(addSyntaxExtension$, { disable: { null: unsupportedMarkdownConstructs } }),
});

/**
 * A WYSIWYG editor that reads and writes markdown, for text that is rendered outside this
 * app as well (the app renders the same content, so we stick to markdown every renderer
 * handles). There are no images and no links: linkPlugin is deliberately not loaded, since it
 * also turns every URL and e-mail address that is typed or pasted into a link, and there is no
 * way to see or change the URL behind a link afterwards. MarkdownViewer renders what this
 * writes.
 *
 * @param {object} props
 * @param {string} [props.label] Shown as the fieldset legend, like a TextField label.
 * @param {string} props.markdown The content to edit.
 * @param {(markdown: string) => void} props.onChange Called on every edit with the markdown.
 * @param {boolean} [props.disabled] Puts the editor in read-only mode.
 * @param {string} [props.placeholder] Shown while the editor is empty.
 */
const MarkdownEditor = ({ label, markdown, onChange, disabled, placeholder }) => {
  const [editorHasFocus, setEditorHasFocus] = useState(false);
  // The editor appends the container for its popups (the block type dropdown) to document.body,
  // where it is painted below the dialog we are rendered in - the dropdown then opens invisibly
  // behind it. Keeping the popups inside our own element puts them in the dialog's stacking
  // context, and inside the dialog's focus trap.
  const [overlayContainer, setOverlayContainer] = useState(null);
  const editorRef = useRef(null);

  const plugins = useMemo(
    () => [
      headingsPlugin({ allowedHeadingLevels: [1, 2, 3] }),
      listsPlugin(),
      quotePlugin(),
      thematicBreakPlugin(),
      markdownShortcutPlugin(),
      plainTextForUnsupportedPlugin(),
      toolbarPlugin({
        toolbarContents: () => (
          <>
            <UndoRedo />
            <Separator />
            {/* no underline, it has no markdown representation */}
            <BoldItalicUnderlineToggles options={["Bold", "Italic"]} />
            <Separator />
            <BlockTypeSelect />
            <ListsToggle options={["bullet", "number"]} />
            <InsertThematicBreak />
          </>
        ),
      }),
    ],
    []
  );

  // The editor only reads the markdown it is given when it mounts, so push in content that
  // arrives later, or a value that was changed from the outside. Comparing trimmed values keeps
  // it from resetting itself over the whitespace the editor and the form disagree about.
  useEffect(() => {
    const editorMarkdown = editorRef.current?.getMarkdown();
    if (editorMarkdown !== undefined && (markdown ?? "").trim() !== editorMarkdown.trim()) {
      editorRef.current.setMarkdown(markdown ?? "");
    }
  }, [markdown]);

  // a click next to the text - in the padding, or below a short text - should put the cursor in
  // the editor, the way clicking anywhere in a TextField does
  const handleClick = (event) => {
    if (!event.target.closest("[contenteditable], .mdxeditor-toolbar")) {
      editorRef.current?.focus(undefined, { defaultSelection: "rootEnd" });
    }
  };

  const handleBlur = (event) => {
    // moving focus between the toolbar and the text is still focus within the editor
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setEditorHasFocus(false);
    }
  };

  return (
    /* this FormControl type="fieldset" will provide the border and the label for the editor */
    <FormControl
      component="fieldset"
      ref={setOverlayContainer}
      onFocus={() => setEditorHasFocus(true)}
      onBlur={handleBlur}
      onClick={handleClick}
      sx={{
        width: "100%",
        padding: 1,
        borderColor: (theme) => (editorHasFocus ? theme.palette.info.main : theme.palette.grey[400]) + " !important",
        "&:hover": {
          borderColor: (theme) => (editorHasFocus ? theme.palette.info.main : "black") + " !important",
        },
        border: editorHasFocus ? 2 : 1,
        borderRadius: 1,
        // the height has to go on the editable element itself, otherwise clicking below the
        // first line lands on the wrapper around it and doesn't put the cursor in the text
        "& .markdownEditorContent": { minHeight: "200px" },
        // the placeholder is laid on top of the editable element, so it must let clicks through
        "& .markdownEditorContent:not([contenteditable])": { pointerEvents: "none" },
        // the editor leaves list indentation to the page, and the app's global reset takes the
        // browser's padding off every element, so the bullets and the numbers would hang outside
        // the text. The same indentation as in MarkdownViewer, so editing looks like the result.
        "& .markdownEditorContent ul, & .markdownEditorContent ol": { paddingLeft: "24px" },
      }}
    >
      {label && (
        <FormLabel
          component="legend"
          sx={{ pl: 1, pr: 1, fontSize: 11, color: (theme) => (editorHasFocus ? theme.palette.info.main : null) }}
        >
          {label}
        </FormLabel>
      )}
      <MDXEditor
        ref={editorRef}
        markdown={markdown ?? ""}
        plugins={plugins}
        readOnly={disabled}
        placeholder={placeholder}
        contentEditableClassName="markdownEditorContent"
        overlayContainer={overlayContainer}
        suppressHtmlProcessing
        onError={({ error, source }) => console.error(`MarkdownEditor could not load the text: ${error}`, source)}
        onChange={(value, initialMarkdownNormalize) => {
          // ignore the tidy-up the parser does to the initial content, it isn't a user edit
          if (!initialMarkdownNormalize) {
            onChange(value);
          }
        }}
      />
    </FormControl>
  );
};

export default MarkdownEditor;
